import {
  Briefcase,
  CircleCheck,
  CircleHelp,
  ClipboardCheck,
  FastForward,
  Film,
  FileEdit,
  Globe,
  Keyboard,
  Mail,
  MessageSquare,
  Palette,
  Pause,
  Phone,
  Play,
  Rewind,
  RotateCcw,
  StickyNote,
  Type,
  User,
  type LucideIcon,
} from "lucide-react";
import * as React from "react";
import { useHistory } from "react-router-dom";
import { toast } from "sonner";

import { MediaPlayer, MediaPlayerHandle } from "@/components/MediaPlayer";
import { Badge, Button, ButtonGroup, confirmDialog, Dialog, Kbd, KbdGroup, Tooltip } from "@/components/ui";
import { useInvalidateVideoProjectEventLogs } from "@/containers/Editor/VideoProjectPage/components/VideoProjectHistoryPanel";
import { cn } from "@/lib/utils";
import { EDITOR_VIDEO_PROJECT_EXTRA_FIELDS, VideoProject, VideoProjectServices } from "@/services/bedrock";
import { editorQueueListUrl, getErrorMessage, printDateToChat, useComplianceChecker } from "@/utils";
import { StylePreferencesOptions } from "@/utils/projectHelpers";

import { FlaggedIssueCard, FlagTimelineMarker } from "./FlaggedIssueCard";
import { clipAppearance, MediaStrip } from "./MediaStrip";
import {
  buildQaIssues,
  clampQaTimestampMs,
  createQaFlagId,
  formatQaTimestamp,
  lastFailComment,
  lastFailTimestampMs,
  LastFailResolution,
  QaFlag,
  timestampMsFromTimelineX,
  unmarkedLastFailIssues,
  unresolvedLastFailIssues,
} from "./qaIssues";
import {
  categoryFromShortcut,
  clampPlayheadSeconds,
  clipForQaIssue,
  hasReadableNotes,
  isTypingTarget,
  isWatchComplete,
  isVideoMedia,
  lastRejectedAudit,
  qaStudioClips,
  QaStudioClip,
  PLAYBACK_RATES,
  PLAYBACK_RATE_SHORTCUTS,
  PlaybackRate,
  QA_SKIP_SECONDS,
  stackSkipCueSeconds,
  srtCuesFromMedia,
  hasTranscriptPollChange,
  shouldPollEditedSpokenTranscripts,
  shouldShowTranscriptRail,
  seekSecondsForTimedLine,
  TimedLine,
} from "./qaStudio";
import { ShortcutsOverlay } from "./ShortcutsOverlay";
import { HudCard, LinkifiedText, PlaybackCue, PlayerInfoRow, SkipCue } from "./studioChrome";
import { TranscriptLyrics } from "./TranscriptLyrics";

const EMPTY_QA_AUDIT_ISSUES: VideoProject.QaAuditIssue[] = [];

type SpotlightRegion = "spec" | "notes" | "client" | "text" | "post" | "source";

const LENS_ICONS: Record<VideoProject.QaIssueCategory, LucideIcon> = {
  ON_SCREEN_AND_POST_TEXT: Type,
  STYLE_PREFERENCES: Palette,
  REQUESTS_AND_NOTES: ClipboardCheck,
  AUDIO_VISUAL: Film,
  OTHER: StickyNote,
};

const LENS_SPOTLIGHT: Record<VideoProject.QaIssueCategory, SpotlightRegion[]> = {
  ON_SCREEN_AND_POST_TEXT: ["text", "post", "client"],
  STYLE_PREFERENCES: ["spec"],
  REQUESTS_AND_NOTES: ["notes"],
  AUDIO_VISUAL: ["source"],
  OTHER: [],
};

interface VideoProjectQaStudioProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  videoProject: VideoProject;
  setVideoProject: React.Dispatch<React.SetStateAction<VideoProject | undefined>>;
  playerNotes?: VideoProject.ClientNote;
  coachNotes?: VideoProject.ClientNote;
  preferencesInfo: StylePreferencesOptions[];
  formatHexColor: (color: string) => string | null;
  videoProjectMessages?: VideoProject.Message[];
  finalMessage: string;
  hashtags: string;
  readOnly?: boolean;
}

export function VideoProjectQaStudio({
  open,
  onOpenChange,
  videoProject,
  setVideoProject,
  playerNotes,
  coachNotes,
  preferencesInfo,
  formatHexColor,
  videoProjectMessages,
  finalMessage,
  hashtags,
  readOnly = false,
}: VideoProjectQaStudioProps) {
  const history = useHistory();
  const invalidateEventLogs = useInvalidateVideoProjectEventLogs();
  const playerRef = React.useRef<MediaPlayerHandle>(null);
  const commentRef = React.useRef<HTMLTextAreaElement>(null);
  const pendingSeekRef = React.useRef<number | null>(null);
  const timelineRef = React.useRef<HTMLDivElement>(null);
  const isMac = React.useMemo(() => {
    if (typeof window === "undefined") return true;
    return /Macintosh|Mac OS X/i.test(navigator.userAgent);
  }, []);
  const modKey = isMac ? "⌘" : "Ctrl";

  const [playbackRate, setPlaybackRate] = React.useState<PlaybackRate>(1);
  const [playing, setPlaying] = React.useState(false);
  const [currentSeconds, setCurrentSeconds] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [maxPlayed, setMaxPlayed] = React.useState(0);
  const [skippedWatch, setSkippedWatch] = React.useState(false);
  const [selectedClipKey, setSelectedClipKey] = React.useState<string | null>(null);
  const [flags, setFlags] = React.useState<QaFlag[]>([]);
  const [lastFailResolutions, setLastFailResolutions] = React.useState<Record<number, LastFailResolution | undefined>>(
    {}
  );
  const [lastFailTimestamps, setLastFailTimestamps] = React.useState<Record<number, number | null | undefined>>({});
  const [lastFailComments, setLastFailComments] = React.useState<Record<number, string | undefined>>({});
  const [selectedFlagId, setSelectedFlagId] = React.useState<string | null>(null);
  const [selectedLastFailId, setSelectedLastFailId] = React.useState<number | null>(null);
  const [highlightUnmarkedLastFail, setHighlightUnmarkedLastFail] = React.useState(false);
  const firstUnmarkedLastFailRef = React.useRef<HTMLDivElement>(null);
  const [spotlight, setSpotlight] = React.useState<VideoProject.QaIssueCategory | null>(null);
  const [showShortcuts, setShowShortcuts] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [playbackCue, setPlaybackCue] = React.useState<{ id: number; playing: boolean } | null>(null);
  const [skipCue, setSkipCue] = React.useState<{ id: number; at: number; seconds: number } | null>(null);
  const [retryingSpoken, setRetryingSpoken] = React.useState(false);
  const [transcriptCollapsed, setTranscriptCollapsed] = React.useState(false);

  const setPlayingWithCue = React.useCallback((next: boolean | ((current: boolean) => boolean)) => {
    setPlaying(current => {
      const resolved = typeof next === "function" ? next(current) : next;
      if (resolved !== current) {
        setPlaybackCue({ id: Date.now(), playing: resolved });
      }
      return resolved;
    });
  }, []);

  React.useEffect(() => {
    if (!open || (selectedFlagId == null && selectedLastFailId == null)) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest("[data-qa-flag-card], [data-qa-flag-marker]")) return;
      setSelectedFlagId(null);
      setSelectedLastFailId(null);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, selectedFlagId, selectedLastFailId]);

  React.useEffect(() => {
    if (!open) return;
    setFlags([]);
    setLastFailResolutions({});
    setLastFailTimestamps({});
    setLastFailComments({});
    setSelectedFlagId(null);
    setSelectedLastFailId(null);
    setHighlightUnmarkedLastFail(false);
    setSpotlight(null);
    setSelectedClipKey(null);
    setSkippedWatch(false);
    setMaxPlayed(0);
    setCurrentSeconds(0);
    setPlaying(false);
    setShowShortcuts(false);
    setPlaybackCue(null);
    setSkipCue(null);
    setTranscriptCollapsed(false);
    pendingSeekRef.current = null;
  }, [open, videoProject.id]);

  const clips = React.useMemo(
    () => qaStudioClips(videoProject.editedMedia, videoProject.originalMedia),
    [videoProject.editedMedia, videoProject.originalMedia]
  );
  const lastFail = lastRejectedAudit(videoProject.qaAudits);
  const lastFailIssues = lastFail?.qaAuditIssues ?? EMPTY_QA_AUDIT_ISSUES;
  const selectedClip =
    clips.find(clip => clip.key === selectedClipKey) ?? clips.find(clip => clip.source === "EDITED") ?? clips[0];
  const activeMedia = selectedClip?.media;
  const canFlag = !readOnly && selectedClip?.source === "EDITED";
  const selectedAppearance = selectedClip ? clipAppearance(selectedClip.source) : undefined;
  const { findBlockedWords, findFlaggedWords } = useComplianceChecker({
    playerId: videoProject.playerId,
    coachId: videoProject.coachId,
  });
  const caption = [finalMessage, hashtags].filter(Boolean).join(" ");
  const captionBlockedWords = React.useMemo(
    () => [...new Set([...findBlockedWords(caption), ...findFlaggedWords(caption)])],
    [caption, findBlockedWords, findFlaggedWords]
  );
  const shouldPollSpoken = shouldPollEditedSpokenTranscripts(videoProject.editedMedia);
  const issues = buildQaIssues(flags);
  const unmarkedLastFail = unmarkedLastFailIssues(lastFailIssues, lastFailResolutions);
  const unresolvedLastFail = unresolvedLastFailIssues(
    lastFailIssues,
    lastFailResolutions,
    lastFailTimestamps,
    lastFailComments
  );
  const hasIssues = issues.length > 0 || unresolvedLastFail.length > 0;
  const watchComplete = isWatchComplete(maxPlayed, skippedWatch);
  const player = videoProject.player;
  const playerName = player?.fullName || videoProject.playerName;
  const playerAliases = player?.aliases?.filter(Boolean) ?? [];
  const regionsAndBranches = [...(player?.regionNames || []), ...(player?.branchNames || [])].filter(Boolean);
  const hasPlayerInfo = Boolean(
    playerName ||
      playerAliases.length ||
      videoProject.companyName ||
      player?.phone ||
      player?.email ||
      player?.webAddress ||
      regionsAndBranches.length
  );

  const mergeTranscriptMedia = React.useCallback(
    (latest: VideoProject) => {
      setVideoProject(prev => {
        if (!prev || !hasTranscriptPollChange(prev, latest)) return prev;
        return {
          ...prev,
          editedMedia: latest.editedMedia ?? prev.editedMedia,
        };
      });
    },
    [setVideoProject]
  );

  React.useEffect(() => {
    if (!open || !shouldPollSpoken) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const latest = await VideoProjectServices.show(videoProject.id, EDITOR_VIDEO_PROJECT_EXTRA_FIELDS);
        if (!cancelled) mergeTranscriptMedia(latest);
      } catch {
        // Keep reviewing even if transcript polling fails.
      }
    };

    void poll();
    const interval = window.setInterval(() => {
      void poll();
    }, 10_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [mergeTranscriptMedia, open, shouldPollSpoken, videoProject.id]);

  const applyUpdatedProject = (updated: VideoProject) => {
    setVideoProject(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        status: updated.status ?? prev.status,
        editorUserId: updated.editorUserId ?? prev.editorUserId,
        qaUserId: updated.qaUserId ?? prev.qaUserId,
        qaStatus: updated.qaStatus ?? prev.qaStatus,
        qaDate: updated.qaDate ?? prev.qaDate,
        qaAudits: updated.qaAudits ?? prev.qaAudits,
      };
    });
    invalidateEventLogs(videoProject.id);
  };

  const runEvent = async (event: VideoProject.Event, nextIssues?: VideoProject.QaIssue[]) => {
    setSaving(true);
    try {
      const updated = await VideoProjectServices.update(videoProject.id, {
        event,
        ...(nextIssues ? { issues: nextIssues } : {}),
      });
      applyUpdatedProject(updated);
      return updated;
    } catch (error) {
      toast.error(getErrorMessage(error) || "Failed to update project");
      return undefined;
    } finally {
      setSaving(false);
    }
  };

  const seekTo = React.useCallback(
    (seconds: number) => {
      const next = clampPlayheadSeconds(seconds, duration || activeMedia?.duration || 0);
      playerRef.current?.seekTo(next);
      setCurrentSeconds(next);
    },
    [duration, activeMedia?.duration]
  );

  const seekToTimedLine = React.useCallback(
    (line: TimedLine) => {
      seekTo(seekSecondsForTimedLine(line));
      setPlayingWithCue(true);
    },
    [seekTo, setPlayingWithCue]
  );

  const retrySpokenTranscript = React.useCallback(async () => {
    if (retryingSpoken) return;
    setRetryingSpoken(true);
    try {
      await VideoProjectServices.retryEditedTranscript(videoProject.id);
      const latest = await VideoProjectServices.show(videoProject.id, EDITOR_VIDEO_PROJECT_EXTRA_FIELDS);
      mergeTranscriptMedia({
        ...latest,
        editedMedia: (latest.editedMedia || videoProject.editedMedia || []).map(media => ({
          ...media,
          transcriptStatus: media.transcriptStatus === "FAILED" ? "WAITING" : media.transcriptStatus,
        })),
      });
    } catch (error) {
      toast.error(getErrorMessage(error) || "Failed to retry spoken transcript");
    } finally {
      setRetryingSpoken(false);
    }
  }, [mergeTranscriptMedia, retryingSpoken, videoProject.editedMedia, videoProject.id]);

  const skipBy = React.useCallback(
    (requestedDelta: number) => {
      const durationSeconds = duration || activeMedia?.duration || 0;
      const next = clampPlayheadSeconds(currentSeconds + requestedDelta, durationSeconds);
      const actualDelta = next - currentSeconds;
      seekTo(next);
      setSkipCue(current => {
        const now = Date.now();
        const seconds = stackSkipCueSeconds(current, actualDelta, now, requestedDelta);
        if (seconds == null) return current;
        return { id: now, at: now, seconds };
      });
    },
    [activeMedia?.duration, currentSeconds, duration, seekTo]
  );

  const selectClip = React.useCallback((clip: QaStudioClip, seekSeconds?: number) => {
    pendingSeekRef.current = seekSeconds ?? null;
    setSelectedClipKey(clip.key);
    setCurrentSeconds(seekSeconds ?? 0);
    setDuration(0);
    setPlaying(false);
  }, []);

  const handleDuration = React.useCallback((nextDuration: number) => {
    setDuration(nextDuration);
    const pending = pendingSeekRef.current;
    if (pending == null) return;
    pendingSeekRef.current = null;
    const next = clampPlayheadSeconds(pending, nextDuration);
    playerRef.current?.seekTo(next);
    setCurrentSeconds(next);
  }, []);

  const focusIssue = React.useCallback(
    (issue: { timestampMs?: number | null }) => {
      const clip = clipForQaIssue(clips);
      const seekSeconds = issue.timestampMs != null ? issue.timestampMs / 1000 : undefined;
      if (clip && clip.key !== selectedClip?.key) {
        selectClip(clip, seekSeconds);
        return;
      }
      if (seekSeconds != null) seekTo(seekSeconds);
    },
    [clips, selectClip, seekTo, selectedClip?.key]
  );

  const addFlag = React.useCallback(
    (category: VideoProject.QaIssueCategory) => {
      if (!canFlag) return;
      const flag: QaFlag = {
        id: createQaFlagId(),
        category,
        comment: "",
        timestampMs: Math.round(currentSeconds * 1000),
      };
      setFlags(current => [...current, flag]);
      setSelectedFlagId(flag.id);
      setSelectedLastFailId(null);
      setSpotlight(category);
      window.setTimeout(() => commentRef.current?.focus(), 0);
    },
    [canFlag, currentSeconds]
  );

  const removeFlag = (id: string) => {
    setFlags(current => current.filter(flag => flag.id !== id));
    setSelectedFlagId(current => (current === id ? null : current));
  };

  const handleSubmit = async () => {
    if (readOnly || saving) return;
    if (unmarkedLastFail.length) {
      setHighlightUnmarkedLastFail(true);
      toast.error("Mark each previous QA issue as resolved or not.");
      window.requestAnimationFrame(() => {
        firstUnmarkedLastFailRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
      return;
    }
    const sendingBack = hasIssues;
    const nextIssues = [...issues, ...unresolvedLastFail];
    const watchedPercent = Math.round(maxPlayed * 100);
    const confirmed = await confirmDialog({
      title: sendingBack ? "Send back for edits?" : "Send to client?",
      description: (
        <div className="space-y-2 text-sm text-slate-600">
          <p>
            {sendingBack
              ? `This will send ${nextIssues.length} issue${nextIssues.length === 1 ? "" : "s"} back to the editor${
                  unresolvedLastFail.length
                    ? `, including ${unresolvedLastFail.length} still unresolved from the last review`
                    : ""
                }.`
              : "This will mark QA as approved and send the project to the client."}
          </p>
          {!watchComplete ? <p>You have not finished watching the cut ({watchedPercent}% watched).</p> : null}
        </div>
      ),
      confirmLabel: sendingBack ? "Send back for edits" : "Send to client",
      cancelLabel: "Keep reviewing",
      destructive: sendingBack,
    });
    if (!confirmed) return;
    if (!watchComplete) setSkippedWatch(true);

    if (sendingBack) {
      const updated = await runEvent(VideoProject.Event.REJECT_QA, nextIssues);
      if (!updated) return;
      toast.success("Project sent back for edits");
      onOpenChange(false);
      history.replace(editorQueueListUrl());
      return;
    }

    const updated = await runEvent(VideoProject.Event.APPROVE_QA);
    if (!updated) return;
    toast.success("Project sent to the client");
    onOpenChange(false);
  };

  React.useEffect(() => {
    if (!open || !showShortcuts) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setShowShortcuts(false);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [open, showShortcuts]);

  React.useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const typing = isTypingTarget(event.target);
      if (!typing && (event.key === "?" || (event.key === "/" && event.shiftKey))) {
        event.preventDefault();
        setShowShortcuts(current => !current);
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        if (showShortcuts) {
          setShowShortcuts(false);
          return;
        }
        if (selectedFlagId && typing) {
          (event.target as HTMLElement).blur();
          return;
        }
        if (selectedFlagId) {
          const flag = flags.find(item => item.id === selectedFlagId);
          if (flag && !flag.comment.trim()) removeFlag(flag.id);
          else setSelectedFlagId(null);
          return;
        }
        if (selectedLastFailId != null) {
          setSelectedLastFailId(null);
          return;
        }
        return;
      }
      const withMod = isMac ? event.metaKey : event.ctrlKey;
      if (withMod && event.key === "Enter") {
        if (readOnly) return;
        event.preventDefault();
        void handleSubmit();
        return;
      }
      if (
        event.key === "Enter" &&
        !event.shiftKey &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        (selectedFlagId || selectedLastFailId != null)
      ) {
        event.preventDefault();
        if (typing) (event.target as HTMLElement).blur();
        setSelectedFlagId(null);
        setSelectedLastFailId(null);
        return;
      }
      if (typing) return;

      if (event.key === " " || event.key === "k" || event.key === "K") {
        event.preventDefault();
        setPlayingWithCue(current => !current);
        return;
      }
      if (event.key === "j" || event.key === "J" || event.key === "ArrowLeft") {
        event.preventDefault();
        skipBy(-QA_SKIP_SECONDS);
        return;
      }
      if (event.key === "l" || event.key === "L" || event.key === "ArrowRight") {
        event.preventDefault();
        skipBy(QA_SKIP_SECONDS);
        return;
      }
      if (!event.metaKey && !event.ctrlKey && !event.altKey) {
        if (event.key === "t" || event.key === "T") {
          if (!shouldShowTranscriptRail(activeMedia, selectedClip?.source)) return;
          event.preventDefault();
          setTranscriptCollapsed(current => !current);
          return;
        }
        const rate = PLAYBACK_RATES.find(
          item => PLAYBACK_RATE_SHORTCUTS[item].toLowerCase() === event.key.toLowerCase()
        );
        if (rate != null) {
          event.preventDefault();
          setPlaybackRate(rate);
          return;
        }
      }
      if ((event.key === "Backspace" || event.key === "Delete") && selectedFlagId) {
        if (readOnly) return;
        event.preventDefault();
        removeFlag(selectedFlagId);
        return;
      }
      const category = categoryFromShortcut(event.key);
      if (category && canFlag && !event.metaKey && !event.ctrlKey && !event.altKey && !event.shiftKey) {
        event.preventDefault();
        addFlag(category);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  const spotlightRegions = spotlight ? LENS_SPOTLIGHT[spotlight] : [];
  const isSpotlighted = (region: SpotlightRegion) => spotlightRegions.includes(region);
  const isCategorySpotlighted = (category: VideoProject.QaIssueCategory) => spotlight === category;
  const durationSeconds = duration || activeMedia?.duration || 0;

  const updateFlagTimestamp = React.useCallback(
    (id: string, timestampMs: number | null, seek = true) => {
      const nextMs = timestampMs == null ? null : clampQaTimestampMs(timestampMs, durationSeconds);
      setFlags(current => current.map(item => (item.id === id ? { ...item, timestampMs: nextMs } : item)));
      if (seek && nextMs != null) seekTo(nextMs / 1000);
    },
    [durationSeconds, seekTo]
  );

  const updateLastFailTimestamp = React.useCallback(
    (id: number, timestampMs: number | null, seek = true) => {
      const nextMs = timestampMs == null ? null : clampQaTimestampMs(timestampMs, durationSeconds);
      setLastFailTimestamps(current => ({ ...current, [id]: nextMs }));
      if (seek && nextMs != null) seekTo(nextMs / 1000);
    },
    [durationSeconds, seekTo]
  );

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="full"
      showCloseButton
      disableEscapeDismissal
      className="flex h-[calc(100vh-2rem)] max-h-[100vh] min-h-0 w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl"
    >
      <Dialog.Header className="shrink-0 px-5 py-3">
        <div className="flex items-center justify-between gap-4 pr-8">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-500">
              <ClipboardCheck className="size-4" />
            </div>
            <Dialog.Title className="min-w-0 text-base font-bold text-slate-800">
              QA review · {videoProject.playerName || "Player"}
            </Dialog.Title>
            {readOnly ? (
              <span className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                View only
              </span>
            ) : null}
          </div>
        </div>
      </Dialog.Header>

      <Dialog.Content className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden p-4 lg:grid-cols-[18rem_minmax(0,1fr)_20rem]">
        <div className="-mx-1 flex min-h-0 flex-col gap-3 overflow-y-auto px-1">
          <div className="flex flex-col gap-1.5">
            {VideoProject.QaIssueCategories.map(category => {
              const meta = VideoProject.QaIssueCategoryMeta[category];
              const Icon = LENS_ICONS[category];
              const active = spotlight === category;
              return (
                <Tooltip key={category}>
                  <Tooltip.Trigger
                    render={
                      <button
                        type="button"
                        title={`${readOnly || !canFlag ? meta.label : `Flag ${meta.label}`} (${meta.shortcut})`}
                        onClick={() => {
                          if (readOnly || !canFlag) {
                            setSpotlight(current => (current === category ? null : category));
                            return;
                          }
                          addFlag(category);
                        }}
                        onMouseEnter={() => setSpotlight(category)}
                        onMouseLeave={() => setSpotlight(current => (current === category ? null : current))}
                        className={cn(
                          "rounded-xl border px-3 py-2 text-left transition-all",
                          !readOnly && !canFlag ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                          active
                            ? "border-amber-400 bg-amber-50"
                            : "border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/60 hover:shadow-sm"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <span className="flex size-6 items-center justify-center rounded bg-slate-100 font-mono text-[11px] text-slate-600">
                            {meta.shortcut}
                          </span>
                          <Icon className="size-3.5 text-slate-400" />
                          <span className="min-w-0 flex-1 text-sm font-semibold text-slate-800">{meta.label}</span>
                          <CircleHelp className="size-3.5 shrink-0 text-slate-400" />
                        </div>
                      </button>
                    }
                  />
                  <Tooltip.Content side="right" align="start" className="max-w-80 px-3 py-2.5">
                    <p className="mb-1.5 font-normal">{meta.description}</p>
                    <ul className="space-y-1 text-left font-normal">
                      {meta.lookFor.map(item => (
                        <li key={item} className="flex gap-1.5">
                          <span className="mt-1.5 size-1 shrink-0 rounded-full bg-background/70" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </Tooltip.Content>
                </Tooltip>
              );
            })}
            {readOnly ? (
              <p className="text-[11px] text-slate-400">Hover a category to spotlight matching evidence.</p>
            ) : !canFlag ? (
              <p className="text-[11px] text-slate-400">Switch to the edited video to flag issues.</p>
            ) : null}
          </div>

          <HudCard
            title="Flagged"
            icon={ClipboardCheck}
            iconClass="text-amber-600"
            action={
              <span className="text-[11px] font-medium text-slate-400">
                {lastFailIssues.length || flags.length
                  ? `${lastFailIssues.length + flags.length} issues`
                  : readOnly
                  ? "No issues"
                  : "Looking good"}
              </span>
            }
          >
            {lastFailIssues.map(issue => {
              const timestampMs = lastFailTimestampMs(issue, lastFailTimestamps);
              const comment = lastFailComment(issue, lastFailComments);
              const focused = selectedLastFailId === issue.id;
              const needsResolution = highlightUnmarkedLastFail && lastFailResolutions[issue.id] == null;
              return (
                <FlaggedIssueCard
                  key={`last-fail-${issue.id}`}
                  label={VideoProject.qaIssueCategoryLabel(issue.category)}
                  comment={comment}
                  timestampMs={timestampMs}
                  durationSeconds={durationSeconds}
                  focused={focused}
                  highlighted={isCategorySpotlighted(issue.category)}
                  needsResolution={needsResolution}
                  cardRef={
                    needsResolution && issue.id === unmarkedLastFail[0]?.id ? firstUnmarkedLastFailRef : undefined
                  }
                  readOnly={readOnly}
                  resolution={lastFailResolutions[issue.id]}
                  commentRef={focused ? commentRef : undefined}
                  onSelect={() => {
                    setSelectedLastFailId(issue.id);
                    setSelectedFlagId(null);
                    focusIssue({ timestampMs });
                  }}
                  onChangeTimestamp={nextMs => {
                    setSelectedLastFailId(issue.id);
                    setSelectedFlagId(null);
                    updateLastFailTimestamp(issue.id, nextMs);
                  }}
                  onUsePlayhead={() => {
                    setSelectedLastFailId(issue.id);
                    setSelectedFlagId(null);
                    updateLastFailTimestamp(issue.id, Math.round(currentSeconds * 1000), false);
                  }}
                  onChangeComment={nextComment =>
                    setLastFailComments(current => ({ ...current, [issue.id]: nextComment }))
                  }
                  onDeselect={() => setSelectedLastFailId(null)}
                  onResolved={() => setLastFailResolutions(current => ({ ...current, [issue.id]: "RESOLVED" }))}
                  onUnresolved={() => setLastFailResolutions(current => ({ ...current, [issue.id]: "UNRESOLVED" }))}
                />
              );
            })}
            {flags.map(flag => {
              const focused = selectedFlagId === flag.id;
              return (
                <FlaggedIssueCard
                  key={flag.id}
                  label={VideoProject.qaIssueCategoryLabel(flag.category)}
                  comment={flag.comment}
                  timestampMs={flag.timestampMs}
                  durationSeconds={durationSeconds}
                  focused={focused}
                  highlighted={isCategorySpotlighted(flag.category)}
                  readOnly={readOnly}
                  canDelete={!readOnly}
                  commentRef={focused ? commentRef : undefined}
                  onSelect={() => {
                    setSelectedFlagId(flag.id);
                    setSelectedLastFailId(null);
                    focusIssue(flag);
                  }}
                  onChangeTimestamp={nextMs => {
                    setSelectedFlagId(flag.id);
                    setSelectedLastFailId(null);
                    updateFlagTimestamp(flag.id, nextMs);
                  }}
                  onUsePlayhead={() => {
                    setSelectedFlagId(flag.id);
                    setSelectedLastFailId(null);
                    updateFlagTimestamp(flag.id, Math.round(currentSeconds * 1000), false);
                  }}
                  onChangeComment={nextComment =>
                    setFlags(current =>
                      current.map(item => (item.id === flag.id ? { ...item, comment: nextComment } : item))
                    )
                  }
                  onDeselect={() => setSelectedFlagId(null)}
                  onDelete={() => removeFlag(flag.id)}
                />
              );
            })}
            {readOnly ? (
              !lastFailIssues.length && !flags.length ? (
                <p className="text-xs text-slate-400">No QA issues on this project.</p>
              ) : null
            ) : !canFlag ? (
              <p className="text-xs text-slate-400">Switch to the edited video to flag issues.</p>
            ) : !lastFailIssues.length && !flags.length ? (
              <p className="text-xs text-slate-400">Press 1–5 to drop a pin at the playhead.</p>
            ) : !flags.length ? (
              <p className="text-xs text-slate-400">Press 1–5 to flag a new issue.</p>
            ) : null}
          </HudCard>
        </div>

        <div className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-slate-800 bg-slate-950 shadow-xs">
          <div className="flex shrink-0 items-center gap-3 border-b border-slate-800 bg-slate-900 px-3 py-2">
            <span
              className={cn(
                "rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                selectedAppearance?.headerClass ?? "bg-indigo-500/15 text-indigo-300"
              )}
            >
              {selectedAppearance?.label ?? "Edited video"}
            </span>
          </div>
          {activeMedia ? (
            <div className="flex min-h-0 flex-1 overflow-hidden">
              <div
                className={cn(
                  "relative flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden bg-black [&_.mediaContainer]:flex [&_.mediaContainer]:h-full [&_.mediaContainer]:w-full [&_.mediaContainer]:items-center [&_.mediaContainer]:justify-center [&_.react-player]:!h-full [&_.react-player]:!w-full [&_video]:h-full [&_video]:max-h-full [&_video]:w-full [&_video]:max-w-full [&_video]:object-contain",
                  isVideoMedia(activeMedia) && "cursor-pointer [&_.mediaContainer]:pointer-events-none"
                )}
                onClick={() => {
                  if (isVideoMedia(activeMedia)) setPlayingWithCue(current => !current);
                }}
              >
                {isVideoMedia(activeMedia) ? (
                  <MediaPlayer
                    key={selectedClip.key}
                    ref={playerRef}
                    media={activeMedia}
                    playing={playing}
                    playbackRate={playbackRate}
                    controls={false}
                    objectFit="contain"
                    onPlayingChange={setPlayingWithCue}
                    onProgress={({ playedSeconds, played }) => {
                      setCurrentSeconds(playedSeconds);
                      if (selectedClip.source === "EDITED") {
                        setMaxPlayed(current => Math.max(current, played));
                      }
                    }}
                    onDuration={handleDuration}
                  />
                ) : activeMedia.uri || activeMedia.thumbnailUri ? (
                  <img
                    src={activeMedia.uri || activeMedia.thumbnailUri}
                    alt=""
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <p className="px-4 text-center text-sm text-slate-300">This file cannot be previewed.</p>
                )}
                {playbackCue ? <PlaybackCue key={playbackCue.id} playing={playbackCue.playing} /> : null}
                {skipCue ? <SkipCue key={skipCue.id} seconds={skipCue.seconds} /> : null}
              </div>
              {shouldShowTranscriptRail(activeMedia, selectedClip?.source) ? (
                <TranscriptLyrics
                  media={activeMedia}
                  source={selectedClip?.source}
                  currentSeconds={currentSeconds}
                  highlighted={isSpotlighted("text")}
                  retrying={retryingSpoken}
                  collapsed={transcriptCollapsed}
                  onToggleCollapsed={() => setTranscriptCollapsed(current => !current)}
                  onSeekLine={seekToTimedLine}
                  onRetrySpoken={() => void retrySpokenTranscript()}
                />
              ) : null}
            </div>
          ) : (
            <p className="flex flex-1 items-center justify-center px-4 text-center text-sm text-slate-500">
              No media is available.
            </p>
          )}
          {clips.length ? (
            <MediaStrip
              clips={clips}
              selectedKey={selectedClip?.key}
              onSelect={selectClip}
              highlighted={isSpotlighted("source")}
            />
          ) : null}
          <div className="flex shrink-0 flex-col gap-2 border-t border-slate-800 bg-slate-900 px-3 py-2">
            <div
              ref={timelineRef}
              className="relative cursor-pointer py-1.5"
              onClick={event => {
                if (!durationSeconds) return;
                const rect = event.currentTarget.getBoundingClientRect();
                const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
                seekTo(ratio * durationSeconds);
              }}
            >
              <div className="relative h-2 rounded-full bg-slate-700">
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-indigo-400"
                  style={{ width: `${durationSeconds ? (currentSeconds / durationSeconds) * 100 : 0}%` }}
                />
                {selectedClip?.source === "EDITED"
                  ? srtCuesFromMedia(activeMedia).map((cue, index) =>
                      !durationSeconds ? null : (
                        <span
                          key={`srt-tick-${index}-${cue.startMs}`}
                          title={formatQaTimestamp(cue.startMs) || undefined}
                          className="absolute top-1/2 h-1.5 w-px -translate-x-1/2 -translate-y-1/2 bg-slate-400"
                          style={{ left: `${(cue.startMs / 1000 / durationSeconds) * 100}%` }}
                        />
                      )
                    )
                  : null}
                {selectedClip?.source === "EDITED" ? (
                  <>
                    {flags.map(flag =>
                      flag.timestampMs == null || !durationSeconds ? null : (
                        <FlagTimelineMarker
                          key={flag.id}
                          title={`${VideoProject.qaIssueCategoryLabel(flag.category)} ${formatQaTimestamp(
                            flag.timestampMs
                          )}`}
                          leftPercent={(flag.timestampMs / 1000 / durationSeconds) * 100}
                          selected={selectedFlagId === flag.id || isCategorySpotlighted(flag.category)}
                          readOnly={readOnly}
                          onSelect={() => {
                            setSelectedFlagId(flag.id);
                            setSelectedLastFailId(null);
                          }}
                          onSeek={() => seekTo(flag.timestampMs! / 1000)}
                          onMove={clientX => {
                            const rect = timelineRef.current?.getBoundingClientRect();
                            if (!rect) return;
                            setPlaying(false);
                            updateFlagTimestamp(flag.id, timestampMsFromTimelineX(clientX, rect, durationSeconds));
                          }}
                        />
                      )
                    )}
                    {lastFailIssues.map(issue => {
                      const timestampMs = lastFailTimestampMs(issue, lastFailTimestamps);
                      if (timestampMs == null || !durationSeconds) return null;
                      return (
                        <FlagTimelineMarker
                          key={`last-fail-${issue.id}`}
                          title={`${VideoProject.qaIssueCategoryLabel(issue.category)} ${
                            formatQaTimestamp(timestampMs) || ""
                          }`}
                          leftPercent={(timestampMs / 1000 / durationSeconds) * 100}
                          selected={selectedLastFailId === issue.id || isCategorySpotlighted(issue.category)}
                          readOnly={readOnly}
                          onSelect={() => {
                            setSelectedLastFailId(issue.id);
                            setSelectedFlagId(null);
                          }}
                          onSeek={() => seekTo(timestampMs / 1000)}
                          onMove={clientX => {
                            const rect = timelineRef.current?.getBoundingClientRect();
                            if (!rect) return;
                            setPlaying(false);
                            updateLastFailTimestamp(issue.id, timestampMsFromTimelineX(clientX, rect, durationSeconds));
                          }}
                        />
                      );
                    })}
                  </>
                ) : null}
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-0.5">
                <Button
                  type="button"
                  size="icon-xs"
                  variant="ghost"
                  className="text-slate-200 hover:bg-slate-800 hover:text-white"
                  title={`Back ${QA_SKIP_SECONDS}s (J or ←)`}
                  aria-label={`Skip back ${QA_SKIP_SECONDS} seconds (J or left arrow)`}
                  onClick={() => skipBy(-QA_SKIP_SECONDS)}
                  disabled={!isVideoMedia(activeMedia)}
                >
                  <Rewind className="size-3.5" />
                </Button>
                <Button
                  type="button"
                  size="xs"
                  variant="ghost"
                  className="text-slate-200 hover:bg-slate-800 hover:text-white"
                  title={playing ? "Pause (Space or K)" : "Play (Space or K)"}
                  aria-label={playing ? "Pause (Space or K)" : "Play (Space or K)"}
                  onClick={() => setPlayingWithCue(current => !current)}
                  disabled={!isVideoMedia(activeMedia)}
                >
                  {playing ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
                  {formatQaTimestamp(currentSeconds * 1000) || "0:00"}
                </Button>
                <Button
                  type="button"
                  size="icon-xs"
                  variant="ghost"
                  className="text-slate-200 hover:bg-slate-800 hover:text-white"
                  title={`Forward ${QA_SKIP_SECONDS}s (L or →)`}
                  aria-label={`Skip forward ${QA_SKIP_SECONDS} seconds (L or right arrow)`}
                  onClick={() => skipBy(QA_SKIP_SECONDS)}
                  disabled={!isVideoMedia(activeMedia)}
                >
                  <FastForward className="size-3.5" />
                </Button>
                <ButtonGroup className="ml-1 rounded-lg border border-slate-700 bg-slate-800/80 p-0.5">
                  {PLAYBACK_RATES.map(rate => (
                    <Button
                      key={rate}
                      type="button"
                      size="xs"
                      variant="ghost"
                      className={cn(
                        "text-slate-300 hover:bg-slate-700 hover:text-white",
                        playbackRate === rate && "bg-slate-700 text-white shadow-xs hover:bg-slate-700 hover:text-white"
                      )}
                      title={`${rate}x playback (${PLAYBACK_RATE_SHORTCUTS[rate]})`}
                      aria-label={`${rate}x playback (${PLAYBACK_RATE_SHORTCUTS[rate]})`}
                      onClick={() => setPlaybackRate(rate)}
                    >
                      {rate}x
                    </Button>
                  ))}
                </ButtonGroup>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span>{watchComplete ? "Watch complete" : `${Math.round(maxPlayed * 100)}% watched`}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="-mx-1 flex min-h-0 flex-col gap-6 overflow-y-auto px-1">
          <HudCard active={isSpotlighted("client")} title="Client Info" icon={User} iconClass="text-emerald-600">
            {hasPlayerInfo ? (
              <div className="flex flex-col gap-1.5">
                {playerName || playerAliases.length ? (
                  <PlayerInfoRow icon={User} title="Name">
                    <div className="flex flex-col gap-0.5">
                      {playerName ? <span>{playerName}</span> : null}
                      {playerAliases.length ? (
                        <span className="text-[11px] font-medium text-slate-500">{playerAliases.join(", ")}</span>
                      ) : null}
                    </div>
                  </PlayerInfoRow>
                ) : null}
                {videoProject.companyName || regionsAndBranches.length ? (
                  <PlayerInfoRow icon={Briefcase} title="Company">
                    <div className="flex flex-col gap-1">
                      {videoProject.companyName ? <span>{videoProject.companyName}</span> : null}
                      {regionsAndBranches.length ? (
                        <div className="flex flex-row flex-wrap items-center gap-1">
                          {regionsAndBranches.map(segment => (
                            <Badge
                              key={segment}
                              variant="outline"
                              className="text-[10px] font-medium text-slate-600 border-slate-100"
                            >
                              {segment}
                            </Badge>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </PlayerInfoRow>
                ) : null}
                {player?.email ? (
                  <PlayerInfoRow icon={Mail} title="Email">
                    <span className="wrap-break-word">{player.email}</span>
                  </PlayerInfoRow>
                ) : null}
                {player?.phone ? (
                  <PlayerInfoRow icon={Phone} title="Phone">
                    {player.phone}
                  </PlayerInfoRow>
                ) : null}
                {player?.webAddress ? (
                  <PlayerInfoRow icon={Globe} title="Website">
                    <span className="wrap-break-word">{player.webAddress}</span>
                  </PlayerInfoRow>
                ) : null}
              </div>
            ) : (
              <p className="py-1 text-center text-xs font-medium text-slate-400">No Client details loaded</p>
            )}
          </HudCard>
          <HudCard active={isSpotlighted("post")} title="Post Info" icon={FileEdit} iconClass="text-indigo-600">
            <div className="flex flex-col gap-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 select-none">Caption</p>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{caption || "No caption"}</p>
            </div>
            {captionBlockedWords.length ? (
              <p className="mt-1 text-[11px] font-medium text-amber-700">
                Blocked words: {captionBlockedWords.join(", ")}
              </p>
            ) : null}
          </HudCard>
          <HudCard active={isSpotlighted("spec")} title="Style Preferences" icon={Palette} iconClass="text-indigo-600">
            <div className="flex flex-wrap gap-1.5">
              {preferencesInfo.length ? (
                preferencesInfo.map(pref => (
                  <span
                    key={pref.key}
                    className={cn(
                      "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold",
                      pref.key.toLowerCase().startsWith("no ")
                        ? "border-slate-200 bg-slate-50 text-slate-400"
                        : "border-indigo-100 bg-indigo-50/50 text-indigo-700"
                    )}
                  >
                    {pref.key}
                    {pref.colors?.length ? (
                      <span className="ml-1 inline-flex gap-0.5">
                        {pref.colors.map((color, index) => (
                          <span
                            key={`${color}-${index}`}
                            className="inline-block size-2.5 rounded-sm border border-slate-200"
                            style={{ backgroundColor: formatHexColor(color) || color }}
                          />
                        ))}
                      </span>
                    ) : null}
                  </span>
                ))
              ) : (
                <span className="text-slate-400">No stored preferences</span>
              )}
            </div>
          </HudCard>
          <HudCard active={isSpotlighted("notes")} title="Client Notes" icon={StickyNote} iconClass="text-emerald-600">
            {hasReadableNotes(playerNotes, coachNotes) ? (
              <div className="flex flex-col gap-4 text-sm text-slate-700">
                {coachNotes?.notes?.trim() ? (
                  <div className="flex flex-col gap-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 select-none">
                      Coach Notes (all players)
                    </p>
                    <LinkifiedText>{coachNotes.notes}</LinkifiedText>
                  </div>
                ) : null}
                {coachNotes?.notes?.trim() && playerNotes?.notes?.trim() ? (
                  <div className="border-t border-slate-200/60" />
                ) : null}
                {playerNotes?.notes?.trim() ? (
                  <div className="flex flex-col gap-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 select-none">
                      Player Notes
                    </p>
                    <LinkifiedText>{playerNotes.notes}</LinkifiedText>
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="py-1 text-center text-xs font-medium text-slate-400">No client notes added yet.</p>
            )}
          </HudCard>
          <HudCard
            active={isSpotlighted("notes")}
            title="Messages"
            icon={MessageSquare}
            iconClass="text-sky-600"
            action={
              videoProjectMessages?.length ? (
                <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-100 font-bold">
                  {videoProjectMessages.length}
                </Badge>
              ) : null
            }
          >
            {videoProjectMessages?.length ? (
              <div className="space-y-2 text-slate-700">
                {videoProjectMessages.map(message => (
                  <div key={message.id} className="space-y-0.5">
                    <p className="text-[10px] font-semibold text-slate-400">
                      {message.userName || message.userRole || "Message"}
                      {message.createdDate ? ` · ${printDateToChat(message.createdDate)}` : ""}
                    </p>
                    <LinkifiedText>{message.content || ""}</LinkifiedText>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400">No messages</p>
            )}
          </HudCard>
        </div>
      </Dialog.Content>

      <Dialog.Footer className="shrink-0 sm:items-center sm:justify-between">
        <button
          type="button"
          title="Keyboard shortcuts (?)"
          className="hidden items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 sm:flex"
          onClick={() => setShowShortcuts(current => !current)}
        >
          <Keyboard className="size-3.5" />
          <Kbd>?</Kbd>
        </button>
        {readOnly ? (
          <p className="text-xs font-medium text-slate-500">
            View only — flagging and decisions are disabled until QA review is in progress.
          </p>
        ) : (
          <Button
            variant={watchComplete ? "default" : "outline"}
            title={hasIssues ? `Send back for edits (${modKey}+Enter)` : `Send to client (${modKey}+Enter)`}
            onClick={() => void handleSubmit()}
            disabled={saving}
            loading={saving}
          >
            {hasIssues ? <RotateCcw /> : <CircleCheck />}
            {hasIssues ? "Send back for edits" : "Send to client"}
            <KbdGroup>
              <Kbd>{modKey}</Kbd>
              <Kbd>Enter</Kbd>
            </KbdGroup>
          </Button>
        )}
      </Dialog.Footer>

      {showShortcuts ? (
        <ShortcutsOverlay readOnly={readOnly} modKey={modKey} onClose={() => setShowShortcuts(false)} />
      ) : null}
    </Dialog>
  );
}
